/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import "../../styles/sidebar.css";
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Box,
    Tooltip,
    useMediaQuery
} from '@mui/material';
import {
    ExpandLess,
    ExpandMore,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../AuthContext';

interface SidebarProps {
    open: boolean;
    onClose: () => void;
    onToggle?: () => void;
}

const DRAWER_WIDTH = 220;
const COLLAPSED_WIDTH = 64;

type LocalIconProps = {
    src: string;
    alt?: string;
    className?: string;
};

const LocalIcon: React.FC<LocalIconProps> = ({ src, alt = "", className }) => (
    <img src={src} alt={alt} className={className} loading="lazy" />
);

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, onToggle }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAdmin, isManager } = useAuth();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [masterOpen, setMasterOpen] = useState(
        location.pathname === '/categories' ||
        location.pathname === '/technologies'
    );

    const [selectedPath, setSelectedPath] = React.useState<string>(location.pathname);

    const handleNavigate = (path: string) => {
        setSelectedPath(path);
        navigate(path);
        if (isMobile) onClose(); // auto close on mobile
    };

    const isSelected = (path: string) => selectedPath === path;
    const masterActive =
        isSelected('/categories') || isSelected('/technologies');

    React.useEffect(() => {
        if (!open) {
            setMasterOpen(false);
        } else {
            if (location.pathname === '/categories' || location.pathname === '/technologies') {
                setMasterOpen(true);
            }
        }
    }, [open, location.pathname]);

    return (
        <Drawer
            variant={isMobile ? "temporary" : "permanent"}
            open={isMobile ? open : true}
            onClose={onClose}
            anchor="left"
            sx={{
                width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: open ? DRAWER_WIDTH : COLLAPSED_WIDTH,
                    boxSizing: 'border-box',
                    mt: '64px',
                    px: 0.5,
                    overflowX: 'hidden',
                    zIndex: isMobile ? 1200 : 'unset',

                    // LIGHT THEME (unchanged)
                    background: 'linear-gradient(180deg, #f7fbff 0%, #fffaf9 100%)',
                    borderRight: '1px solid rgba(16,24,40,0.04)',

                    // DARK MODE
                    '@media (prefers-color-scheme: dark)': {
                        background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
                        borderRight: '1px solid rgba(255,255,255,0.08)',
                    },
                },
            }}
        >
            <Box className={`sidebar-toggle-wrapper ${open ? 'open' : 'collapsed'}`}>
                <IconButton
                    size="small"
                    className="sidebar-toggle-btn"
                    onClick={() => {
                        if (onToggle) onToggle();
                        else onClose();
                    }}
                >
                    <img
                        src={open ? "/icons/MenuCollapseIcon.svg" : "/icons/MenuExpandicon.svg"}
                        alt="Toggle sidebar"
                        className="sidebar-toggle-icon"
                    />
                </IconButton>
            </Box>

            <List className={`sidebar-list ${open ? 'open' : 'collapsed'}`}>


                {isAdmin && (
                    <>
                        <ListItem disablePadding className="sidebar-item-wrapper">
                            <ListItemButton
                                onClick={() => setMasterOpen(prev => !prev)}
                                className={`sidebar-item 
                                    ${open ? 'expanded' : 'collapsed'} 
                                    ${masterActive ? 'active' : ''}`}
                            >
                                <ListItemIcon className="sidebar-icon-column">
                                    {open ? (
                                        <Box className={`sidebar-icon-box ${masterActive ? 'active' : ''}`}>
                                            <LocalIcon src="/icons/Master.svg" className="sidebar-local-icon" />
                                        </Box>
                                    ) : (
                                        <Tooltip title="Master" placement="right" arrow>
                                            <Box className={`sidebar-icon-box ${masterActive ? 'active' : ''}`}>
                                                <LocalIcon src="/icons/Master.svg" className="sidebar-local-icon" />
                                            </Box>
                                        </Tooltip>
                                    )}
                                </ListItemIcon>

                                {open && (
                                    <ListItemText
                                        className="sidebar-text"
                                        primary="Master"
                                        secondary="Manage Master Data"
                                    />
                                )}

                                {open && (masterOpen ? <ExpandLess /> : <ExpandMore />)}
                            </ListItemButton>
                        </ListItem>

                        {(open && masterOpen) || !open ? (
                            <Box className="sidebar-children">
                                {['/categories', '/technologies'].map((path) => (
                                    <ListItem key={path} disablePadding className="sidebar-item-wrapper">
                                        <ListItemButton
                                            onClick={() => handleNavigate(path)}
                                            className={`sidebar-item 
                                                ${open ? 'expanded' : 'collapsed'} 
                                                ${isSelected(path) ? 'active' : ''}`}
                                        >
                                            <ListItemIcon className="sidebar-icon-column">
                                                <Tooltip title={path === '/categories' ? 'Categories' : 'Technologies'} placement="right" arrow>
                                                    <Box className={`sidebar-icon-box ${isSelected(path) ? 'active' : ''}`}> 
                                                        <LocalIcon
                                                            src={path === '/categories'
                                                                ? "/icons/Categories.svg"
                                                                : "/icons/Technologies.svg"}
                                                            className="sidebar-local-icon"
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            </ListItemIcon>

                                            {open && (
                                                <ListItemText
                                                    className="sidebar-text"
                                                    primary={path === '/categories' ? "Categories" : "Technologies"}
                                                    secondary={path === '/categories'
                                                        ? "Create & Manage Categories"
                                                        : "View Technologies"}
                                                />
                                            )}
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </Box>
                        ) : null}
                    </>
                )}

                {(isAdmin || isManager) && ['activities', 'assign-activity'].map((item) => (
                    <ListItem key={item} disablePadding className="sidebar-item-wrapper">
                        <ListItemButton
                            onClick={() => handleNavigate(`/${item}`)}
                            className={`sidebar-item 
                                ${open ? 'expanded' : 'collapsed'} 
                                ${isSelected(`/${item}`) ? 'active' : ''}`}
                        >
                            <ListItemIcon className="sidebar-icon-column">
                                {open ? (
                                    <Box className={`sidebar-icon-box ${isSelected(`/${item}`) ? 'active' : ''}`}>
                                        <LocalIcon
                                            src={item === 'activities'
                                                ? "/icons/Activities.svg"
                                                : "/icons/AssignActivity.svg"}
                                            className="sidebar-local-icon"
                                        />
                                    </Box>
                                ) : (
                                    <Tooltip title={item === 'activities' ? 'Activities' : 'Assign Activity'} placement="right" arrow>
                                        <Box className={`sidebar-icon-box ${isSelected(`/${item}`) ? 'active' : ''}`}>
                                            <LocalIcon
                                                src={item === 'activities'
                                                    ? "/icons/Activities.svg"
                                                    : "/icons/AssignActivity.svg"}
                                                className="sidebar-local-icon"
                                            />
                                        </Box>
                                    </Tooltip>
                                )}
                            </ListItemIcon>

                            {open && (
                                <ListItemText
                                    className="sidebar-text"
                                    primary={item === 'activities' ? "Activities" : "Assign Activity"}
                                    secondary={item === 'activities'
                                        ? "Create & manage Activities"
                                        : "Assign Activities"}
                                />
                            )}
                        </ListItemButton>
                    </ListItem>
                ))}

                <ListItem disablePadding className="sidebar-item-wrapper">
                    <ListItemButton
                        onClick={() => handleNavigate('/my-activity')}
                        className={`sidebar-item 
                            ${open ? 'expanded' : 'collapsed'} 
                            ${isSelected('/my-activity') ? 'active' : ''}`}
                    >
                        <ListItemIcon className="sidebar-icon-column">
                            {open ? (
                                <Box className={`sidebar-icon-box ${isSelected('/my-activity') ? 'active' : ''}`}>
                                    <LocalIcon src="/icons/MyActivity.svg" className="sidebar-local-icon" />
                                </Box>
                            ) : (
                                <Tooltip title="My Activity" placement="right" arrow>
                                    <Box className={`sidebar-icon-box ${isSelected('/my-activity') ? 'active' : ''}`}>
                                        <LocalIcon src="/icons/MyActivity.svg" className="sidebar-local-icon" />
                                    </Box>
                                </Tooltip>
                            )}
                        </ListItemIcon>

                        {open && (
                            <ListItemText
                                className="sidebar-text"
                                primary="My Activity"
                                secondary="Track & Act on my Activities"
                            />
                        )}
                    </ListItemButton>
                </ListItem>

            </List>
        </Drawer>
    );
};

export default Sidebar;